/**
 * Format specs for agent prompt responses. These are injected into prompts as
 * HTML comments (<!-- -->) so the model receives precise structure instructions
 * without displaying them in the UI.
 */

/** Format instruction for the medical history response. Inlined as a comment in the user prompt. */
export const getMedicalHistoryResponseFormat = (patientName: string) => `<!--
Generate a comprehensive medical records for patient ${patientName}.

## Workflow

### Step 1: Message Retrieval
Retrieve all messages associated with the **current patient** in the conversation that have an OPEN or UNREAD state using the conversation agent.

### Step 2: Medical Record Retrieval
Transfer to the timeline agent and fetch the complete medical record.

### Step 3: Follow-up Processing

**Step 3.1 — Identification:**

Identify outstanding follow-up items from the following sources:
1. Appointment-associated SOAP medical notes and discharge notes in Step 2
2. Messages retrieved in Step 1

**Step 3.2 — Consolidation and Reconciliation:**

- Perform data consolidation and reconciliation for overlapping or conflicting follow-up items across sources
- **The more recent information shall take precedence**

**Step 3.3 — Resolution Rule (Strict Suppression — Zero Tolerance):**

If **ANY** source indicates a follow-up is completed, resolved, canceled, closed, no longer needed, or similar, that item **must be completely removed** from the output.
Treat it as if the item never existed.

**Example:**
- SOAP note states: "Please drop off the urine sample at the lab"
- Message states: "Client already dropped off urine sample"
- **RESULT:** The urine sample follow-up is **COMPLETELY SUPPRESSED** and does NOT appear in the Follow-ups section at all.

**Step 3.4 — Exclusions:**

Do not surface follow-up items that were **generated or suggested by AI**.

**Step 3.5 — Output Format:**

Format each remaining follow-up item as:
- **Bolded title**
- Followed by **one to two concise sentences** of clinical detail

## Output Requirements

**CRITICAL:** Strictly adhere to the output structure below. Do not include sections such as "Appointment" or "Summary."

## 1. Signalment (🐾)
- Patient name (no hashId)
- Client name (no hashId)
- Key demographics: species, breed, date of birth, age, current weight and measured date, active/deceased status
- Clickable link to complete timeline in Rhapsody PIMS

## 2. Active Problem (⚠️)
- List problem logs and diagnoses which are NOT in resolved or removed state
- Include status and creation date
- From SOAP medical notes, extract the relevant diagnostics, treatment plan, and monitoring plan if applicable
- Group recurring problems together

## 3. Diagnostic Indicators (🔬)
- Diagnostic tests: List lab test and imaging entities within 12 months from today
- Include: name(embed the hyperlink), order date, and short summary based on relevant SOAP medical notes and lab/imaging entity notes section
- Show results in table format

## 4. Key Alerts (🚨)
- From all appointment-associated SOAP, list drug reactions and behavioral considerations for handling. Do not surface other findings.
- List alerts from patient entity in previous client tool call

## 5. Current Medications (💊)
- List prescriptions prescribed within 12 months from today and NOT in draft or canceled state
- Include: full name, prescribed on date, number of refills, refills remaining, last fill date, and instructions
- Do not list Rx numbers
- Sort by most recent first

## 6. Other Medications (💊)
- List prescriptions not mentioned in the Current Medication section, plus any veterinary diet, supplements, and nutraceuticals
- Only show full name for each item by bullet format

## 7. Pending Items (📌)

### Reminders

- Only include reminders that meet one of criteria. 
  - state is overdue.
  - state is open AND due status is due_soon.
- Show results by bulletin points. 
- Strict Suppression Rule (Zero Tolerance)
  If a reminder due status is due_later, it must:
  - Be completely excluded from the output  
  - Not appear in any section

### Follow-ups
- Display results generated in **Step 3** only.
-->`
